import React, { useEffect, useState } from 'react';
import { RouteComponentProps, Prompt, Link, useLocation } from 'react-router-dom';
import {Button, CircularProgress, Backdrop, Typography, Breadcrumbs} from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { withRouter } from 'react-router';

import AdminLayout from '../../../../layouts/AdminLayout';
import { SpecPaper } from '../../../../components/Paper';
import Spec from '../../../../models/Spec';
import CourseWork from '../../../../models/CourseWork';
import utils from '../../../../libs/utils';
import { useDialog } from '../../../../context/Dialog';
import useSnackbar from '../../../../hooks/useSnackbar';
import Course from '../../../../models/Course';
import { ClassroomResourceLink } from '../../../../components/Links';
import { TitleHeader } from '../../../../components/Headers';

interface Props extends RouteComponentProps<{ assignment_id: string; course_id: string }> {}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    backdrop: {
      zIndex: theme.zIndex.drawer + 1,
      color: '#fff',
    },
    root: {
      '& > *': {
        margin: theme.spacing(1),
      },
    },
    input: {
      display: 'none',
    },
  })
);

const AssignmentSpecs = (props: Props) => {
  const {
    assignment_id: courseWorkId,
    course_id: courseId
  } = props.match.params;

   const {state} = useLocation();

  const [specList, setSpecList] = useState<Spec[]>([]);
  const [existingSpecList, setExistingSpecList] = useState<Spec[]>([]);
  const [specChangeList, setSpecChangeList] = useState<{original: Spec, update: Spec}[]>([]);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isSaveAllChanges, setIsSaveAllChanges] = useState<boolean>(false);
  const [courseWork, setCourseWork] = useState<CourseWork | null>(null);
  const [course, setCourse] = useState<Course|null>(null);
  const [courseWorkName, setCourseWorkName] = useState<string>(
    (state as any).courseWork.name
  );


  const classes = useStyles();
  const { confirm } = useDialog();
  const { renderSnackbar, showMessage } = useSnackbar();

  const addBlankSpec = () => {
    const newSpec = new Spec();
    const newSpecIndex = specList.length + 1;
    newSpec.assignmentOrderIndex = newSpecIndex - 1;
    newSpec.id = `UNTITLED-QUESTION-${newSpecIndex}`;
    newSpec.addItem();
    setSpecList([...specList, newSpec]);
  };

  const handleDeleteSpec = (index: number) => {
    setSpecChangeList(specChangeList.filter(a => a.original !== specList[index]));
    specList.splice(index, 1);
    setSpecList([...specList]);
  };

  const handleSpecChange = (origSpec: Spec, updatedSpec: Spec) => {
    const _origSpec = new Spec({...JSON.parse(JSON.stringify(origSpec)), assignmentID: undefined});
    const _updatedSpec = new Spec({...JSON.parse(JSON.stringify(updatedSpec)), assignmentID: undefined});
    const newSpecChangeList = specChangeList.filter(a => a.original !== origSpec);
    const exists = existingSpecList.some(a => a === origSpec);
    if (!exists || (JSON.stringify(_origSpec) !== JSON.stringify(_updatedSpec))) {
      newSpecChangeList.push({
        original: origSpec,
        update: updatedSpec
      });
    }
    setSpecChangeList(newSpecChangeList);
  };

  const handleSpecSaved = (origSpec: Spec, savedSpec: Spec) => {
    setExistingSpecList(existingSpecList.map(spec => {
      if (spec === origSpec) return savedSpec;
      return spec;
    }));

    setSpecChangeList(specChangeList.filter(change => {
      return change.original !== origSpec;
    }));

    setSpecList(specList.map(spec => {
      if (spec === origSpec) return savedSpec;
      return spec;
    }));
  };

  const saveAllSpecChanges = async () => {
    if (await confirm('Do you want to save all the changes made?')) {
      try {
        setIsSaveAllChanges(true);
        await Promise.all(specChangeList.map(change => {
          return (async () => {
            const exists = existingSpecList.some(a => a === change.original);
            const currentSpec = change.update;

            if (exists) {
              if (change.original.id === currentSpec.id) {
                await currentSpec.update();
                return;
              }

              // delete original if it exists and save to register new id
              await Spec.delete(change.original.id);
            }

            await currentSpec.save();
            if (courseWork) {
              await courseWork.addSpec(currentSpec.id);
            }
          })();
        }));

        const newSpecList = specList.map((spec: Spec): Spec => {
          for (let change of specChangeList) {
            if (change.original === spec) {
              return change.update;
            }
          }
          return spec;
        });
        setSpecChangeList([]);
        setExistingSpecList(newSpecList);
        setSpecList(newSpecList);
        showMessage('Changes has been successfully saved.', 'success');
      }
      catch (e) {
        showMessage(utils.getRequestErrorMessage(e), 'error');
      }
      finally {
        setIsSaveAllChanges(false);
      }
    }
  };

  const setAssignmentSpecIndex = async (spec: Spec, targetIndex: number) => {
    try {
      const newSpecList = [...specList];
      const newSpecOrder: string[] = newSpecList.map(a => a.id);
      const currentIndex = newSpecOrder.indexOf(spec.id);

      if (currentIndex < 0)
        throw new Error('Spec is not in the spec list of assignment.');

      utils.swapArrayItems(newSpecOrder, targetIndex, currentIndex);
      utils.swapArrayItems(newSpecList, targetIndex, currentIndex);
      setSpecList(newSpecList);

      if (courseWork) await courseWork.setSpecs(newSpecOrder.filter(a => existingSpecList.some(b => b.id === a)));
      showMessage('Spec order has been saved.', 'success');
    }
    catch (e) {
      showMessage(`Error updating spec order: ${e.message}`, 'error');
    }
  };

  const renderSpecs = () => {
    return specList.map((spec, index) => (
      <div key={`${spec.id}-${index}`} className="mb-5">
        <SpecPaper
          spec={spec}
          existingSpecList={existingSpecList}
          onDeleteSpec={() => handleDeleteSpec(index)}
          onSpecChanged={(updatedSpec) => handleSpecChange(spec, updatedSpec)}
          onSpecSaved={(savedSpec) => handleSpecSaved(spec, savedSpec)}
          onMoveUp={(index ? () => setAssignmentSpecIndex(spec, index - 1) : undefined)}
          onMoveDown={(index + 1) < specList.length ? () => setAssignmentSpecIndex(spec, index + 1) : undefined}
        />
      </div>
    ));
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      setCourseWork(null);
      setSpecList([]);

      // const [_course, _courseWork] = await Promise.all([
      //   Course.findById({courseId}),
      //   CourseWork.findOne({
      //     courseId: courseId,
      //     id: courseWorkId
      //   })
      // ]);
      const loadCourseWorkSpecs = async () => {
        const data = await Spec.find({
          assignmentID: courseWorkId
        }); console.log(data, 'data');
        // setCourseWorkSpecs(data);
        return data;
      };
      const specs = await loadCourseWorkSpecs();
      // const _specs = await _courseWork.getSpecs();
      // setCourse(_course);
      // setCourseWork(_courseWork);
      setExistingSpecList(specs);
      setSpecList(specs);
      // if (!_specs.length) addBlankSpec();
    } catch (e) {
      setErrorMessage(utils.getRequestErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => await loadData())();
  }, []);

  return (
    <AdminLayout>
      { renderSnackbar() }

      <Prompt
        when={!!specChangeList.length}
        message={() => 'You have unsaved changes, do you want to leave this page?'}
      />

      <div className="d-flex align-items-end pt-4">
        <div className="flex-fill">
          <TitleHeader
            breadcrumb={
              <Breadcrumbs aria-label="breadcrumb">
                <Link to={Course.getListUrlPath('admin')}>Courses</Link>
                <Typography>{course?.fullname || courseId}</Typography>
                <Link to={CourseWork.getListUrlPath(courseId, 'admin')}>Assignments</Link>
                <Typography>{courseWork?.name || courseWorkId}</Typography>
                <Typography>Specs</Typography>
              </Breadcrumbs>
            }
          >
            <Typography variant="h5">
              <span>{courseWorkName || courseWorkId} Specs </span>
              {!!courseWork && <ClassroomResourceLink resource={courseWork} />}
            </Typography>
          </TitleHeader>
        </div>

        {!!specChangeList.length && (
          <Button
            variant="contained"
            color="primary"
            onClick={saveAllSpecChanges}
          >
            Save All Changes
          </Button>
        )}
      </div>

      {(() => {
        if (isLoading)
          return (
            <div className="text-center p-4">
              <CircularProgress color="primary" />
              <p className="m-0">Loading Specs</p>
            </div>
          );

        if (errorMessage)
          return (
            <div className="text-center p-4">
              <Typography variant="h5">Something went wrong</Typography>
              <p className="m-0">{ errorMessage }</p>
              <Button
                variant="contained"
                color="primary"
                onClick={loadData}>
                Try Again
              </Button>
            </div>
          );

        return (
          <>
            <div>{renderSpecs()}</div>

            <div className="text-center pb-4">
              <Button color="primary" onClick={addBlankSpec}>
                ADD ITEM
              </Button>
            </div>
          </>
        );
      })()}

      <Backdrop className={classes.backdrop} open={isSaveAllChanges}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </AdminLayout>
  );
};

export default withRouter(AssignmentSpecs);
